/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-trailing-spaces */
/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, of, ReplaySubject, throwError } from 'rxjs';
import { RestService } from '../rest/rest.service';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { tap, catchError, switchMap, map, withLatestFrom, finalize, filter, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as fromApp from '../../app.reducer';
import * as AuthActions from './store/auth.actions';
import * as UserActions from '../user/store/user.actions';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { StorageService } from '../storage/storage.service';
import { AuthRedirectService } from './auth-redirect.service';
import { Token, TokenMaker, UserLogin } from './auth.types';
import { SocketService } from '../socket/socket.service';
import { UtilService } from '../util/util.service';
import { AlertService } from '../alert/alert.service';
import { ToastService } from '../toast/toast.service';
import { RestError } from '../rest/rest.types';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TokenManagerService } from './token-manager.service';
import { ThemeService } from '../theme/theme.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private tokenRenewalUrl = environment.base_api_url + '/auth/jwt/refresh/';
	private reAuthUserUrl = environment.base_api_url + '/auth/jwt/create/';
	private storeSubscription: Subscription;
	private _token: Token;
	private _tokenStorageSubscription: Subscription;
	private _renewAuthFromSocketSubscription: Subscription;

	/**
	 * Emits true once the initial auth check from storage is complete.
	 * Guards should wait for this before checking token state to avoid race conditions.
	 */
	private _authReadySubject = new ReplaySubject<boolean>(1);
	public authReady$ = this._authReadySubject.asObservable();

	constructor(
		private restService: RestService,
		private http: HttpClient,
		private store: Store<fromApp.AppState>,
		private router: Router,
		private storageService: StorageService,
		private socketService: SocketService,
		private util: UtilService,
		private alertService: AlertService,
		private toastService: ToastService,
		private authRedirectService: AuthRedirectService,
		private tokenManager: TokenManagerService,
		private themeService: ThemeService
	) {
		// Set this AuthService reference on TokenManagerService to break circular dependency
		this.tokenManager.setAuthService(this);
		this.init();
	}

	public get token() {
		return this._token;
	}

	public set token(token: Token | undefined) {
		/* saves token to storage via auth.effects.ts */
		this.store.dispatch(new AuthActions.ValidToken(token));
		this._token = token;
	}

	public generateAuthToken(data: UserLogin): Observable<any> {
		return this.restService.post(environment.base_api_url + '/auth/jwt/create/', data).pipe(
			switchMap((response) => {
				if (response instanceof RestError) {
					const httpError = response.error;
					const detail = httpError?.error?.detail || 'Authentication failed';
					return throwError({ status: httpError?.status, detail });
				}
				return of(this.handleTokenSuccess(TokenMaker.create(response)));
			})
		);
	}

	/**
	 * Perform the actual token renewal.
	 * Note: This method should be called via TokenManagerService.refreshToken() to ensure
	 * proper coordination with request queuing. Direct calls are only for internal use.
	 *
	 * @param storedRefresh Optional refresh token to use (for startup refresh from storage)
	 * @param tokenType Optional token type ('Bearer' for OAuth, undefined for JWT)
	 */
	public renewToken(storedRefresh?: string, tokenType?: string): Observable<any> {
		console.log('renewToken()');

		if (!this.token && !storedRefresh) {
			return throwError(() => new Error('No refresh token available'));
		}

		const refreshToken = storedRefresh || this.token.refresh;
		const effectiveTokenType = tokenType || this.token?.token_type;

		// OAuth refresh flow (for social sign in)
		if (effectiveTokenType?.toLowerCase() === 'bearer') {
			console.log('Using OAuth refresh flow');
			return this.refreshOAuthToken(refreshToken);
		}

		// JWT refresh flow (for email login)
		console.log('Using JWT refresh flow');
		return this.restService.post(this.tokenRenewalUrl, { refresh: refreshToken }).pipe(
			switchMap((body: any) => {
				if (body instanceof RestError) {
					return this.handleTokenError(body.error);
				}
				if (!body?.access || !body?.refresh) {
					return this.handleTokenError(body);
				}
				return of(TokenMaker.create(body));
			}),
			map((token: Token) => this.handleTokenSuccess(token))
		);
	}

	private refreshOAuthToken(refreshToken: string): Observable<any> {
		const body = {
			grant_type: 'refresh_token',
			client_id: environment.server_oauth_client_id,
			client_secret: environment.server_oauth_client_secret,
			refresh_token: refreshToken,
		};

		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		return this.http
			.post(`${environment.base_api_url}/auth/oauth/token/`, body, {
				headers: headers,
			})
			.pipe(
				switchMap((response: any) => {
					if (!response?.access_token || !response?.refresh_token) {
						return this.handleTokenError(response);
					}
					// Transform OAuth response to Token format
					// Backend returns: { access_token, refresh_token, expires_in, token_type, scope }
					// AuthService expects: { access, refresh, token_type }
					const token = {
						access: response.access_token,
						refresh: response.refresh_token,
						token_type: response.token_type,
					};
					return of(TokenMaker.create(token));
				}),
				map((token: Token) => this.handleTokenSuccess(token))
			);
	}

	/**
	 * Log out the current user.
	 * @param options.silent If true, don't show the logout toast (used when logout is due to session expiry)
	 */
	public logout(options?: { silent?: boolean }) {
		this.store.dispatch(new AuthActions.Logout());
		this.store.dispatch(new UserActions.Logout());
		this.authRedirectService.clearRedirectUrl();
		this.themeService.clearPreference();
		this._reportLogoutToBackend();
		if (!options?.silent) {
			this.toastService.show('Successfully logged out');
		}
	}

	private _reportLogoutToBackend() {
		console.log('_reportLogoutToBackend()');
		// Fern adding?
		// const logoutResponse = this.restService.get(environment.base_api_url + '/auth/jwt/clear').toPromise();
		// console.log('logoutResponse:');
		// console.log(logoutResponse);
	}

	private init() {
		this.startAuthSubscription();
		this.checkStorageForToken();
	}

	private startAuthSubscription() {
		this.storeSubscription = this.store.select('auth').subscribe((authState) => {
			const hadToken = this._token != null;
			const hasNewToken = authState.access && authState.refresh;
			const isNewLogin = !hadToken && hasNewToken;
			const isTokenUpdate = hadToken && hasNewToken && this._token.access !== authState.access;

			if (isNewLogin || isTokenUpdate) {
				console.log('user logged in! Should get user & open socket');
				this._token = TokenMaker.create(authState);
				this.socketService.init();
				this.store.dispatch(new UserActions.GetUser());
			} else if (this._token != null && !authState.access && !authState.refresh) {
				this.storageService.clearAuthStorage();
				this.socketService.stopSocket('onLogout');
				this._token = null;
				this.router.navigate(['/home']);
			}
		});

		/* Listen to token renewal requests from the Socket */
		/* Process the renewal & update ngrx auth store */
		this._renewAuthFromSocketSubscription = this.socketService.socketAuthSubject.subscribe(() => {
			console.log('chat socket requesting a renewal flow....');
			// Use TokenManagerService to coordinate refresh with other pending requests
			this.tokenManager.refreshToken().subscribe({
				next: (accessToken: string) => {
					console.log('renewToken response after socket requested it');
					console.log(accessToken);
					// Token is already saved by handleTokenSuccess in renewToken
					/* need to refresh tab2 chat threads */
				},
				error: (err: any) => {
					console.warn('Token renewal from socket failed - session expired');
					console.log(err);
					/* Token refresh failed (likely refresh token also expired after being idle).
					   handleTokenError already handles logout for auth errors via RestService,
					   but OAuth refresh uses HttpClient directly, so we need to logout here
					   if the token still exists (meaning handleTokenError didn't already logout). */
					if (this._token) {
						this.logout({ silent: true });
						this.toastService.show('Session expired. Please log in again.');
					}
				},
			});
		});
	}

	private checkStorageForToken() {
		this._tokenStorageSubscription = this.storageService.storedTokenSubject.subscribe((data) => {
			if (typeof data === 'undefined') {
				// Storage check not yet complete, wait for next emission
				return;
			}
			if (data === null) {
				console.log('No stored token exists..');
				// Signal that auth check is complete (no token found)
				this._authReadySubject.next(true);
				return;
			}

			if (data instanceof Token) {
				const t = data.access;
				const r = data.refresh;
				const isOAuthToken = data.token_type?.toLowerCase() === 'bearer';

				// For OAuth tokens, we cannot check expiry locally (opaque tokens),
				// so proactively refresh to ensure a valid session
				const shouldRefresh = this.util.tokenExpired(t) || isOAuthToken;

				if (shouldRefresh) {
					console.log(isOAuthToken
						? 'OAuth token detected, proactively refreshing...'
						: 'token is expired!!!!');
					// Use TokenManagerService to coordinate refresh with other pending requests
					// This ensures all API calls wait for this refresh to complete
					this.tokenManager.refreshToken(r, data.token_type).subscribe({
						next: (accessToken: string) => {
							console.log('completed checkStorageForToken renewAuthToken');
							// Signal that auth check is complete (token refreshed)
							this._authReadySubject.next(true);
						},
						error: (err: any) => {
							console.error('Cold start token refresh failed:', err);
							// For OAuth tokens, if refresh fails, the token might still be valid
							// Try to use it anyway and let the 401 interceptor handle it if needed
							if (isOAuthToken && !this.util.tokenExpired(t)) {
								console.log('OAuth refresh failed, attempting to use existing token...');
								this.handleTokenSuccess(data);
							}
							// handleTokenError already handles logout for auth errors
							// Signal that auth check is complete (refresh failed)
							this._authReadySubject.next(true);
						}
					});
				} else {
					console.log('token is valid!!!');
					this.handleTokenSuccess(data);
					// Signal that auth check is complete (valid token restored)
					this._authReadySubject.next(true);
				}
			}
		});
	}

	private handleTokenError(error: any): Observable<never> {
		console.error('Error renewing auth token:');
		if (error) {
			console.log(error);
		}

		const status = error?.status ?? error?.error?.status;
		const code = error?.code ?? error?.error?.code;
		const detail = error?.detail ?? error?.error?.detail;

		// Determine the type of auth error for better messaging
		const isBlacklistedToken = code === 'token_not_valid' && detail?.includes('blacklist');
		const isExpiredToken = code === 'token_not_valid' && detail?.includes('expired');
		const isInvalidUser = code === 'invalid_user' || code === 'user_not_found';
		const isAuthError = status === 401 || code === 'token_not_valid' || isInvalidUser;

		// Create a user-friendly error message
		let userMessage: string;
		if (isBlacklistedToken) {
			userMessage = 'Your session has expired. Please log in again.';
		} else if (isExpiredToken) {
			userMessage = 'Your session has expired. Please log in again.';
		} else if (isInvalidUser) {
			userMessage = 'Your account could not be verified. Please log in again.';
		} else if (isAuthError) {
			userMessage = 'Session expired. Please log in again.';
		} else {
			userMessage = 'An error occurred. Please try again.';
		}

		if (isAuthError) {
			// Don't show generic alert for auth errors - the logout() will handle navigation
			// Use silent logout since the caller (interceptor) will show an appropriate toast
			this.logout({ silent: true });
		} else {
			// Only show alert for non-auth errors
			this.alertService.presentFailedUpdateAlert(error);
		}

		return throwError(() => new Error(userMessage));
	}

	private handleTokenSuccess(e: Token): Token {
		console.log('handleTokenSuccess()');
		console.log(e);
		if (e && e.access) {
			this.token = e as Token; /* this setter dispatches ValidToken() */
			console.log('access', e.access);
			console.log('refresh', e.refresh);
		} else {
			this.token = undefined;
		}
		return e as Token;
	}
}
